/**
 * GeoTileMap — the geographic basemap the Map View and the Track page draw their cameras on.
 *
 * Drawn with MapLibre GL: the map is rendered by the GPU, zooms continuously and animates
 * every move, instead of swapping grids of picture tiles a whole zoom level at a time. That is
 * the difference an operator feels as "smooth".
 *
 * A thin, opinionated wrapper that exists to do five things the boards should not have to
 * know about:
 *
 *  1. LOAD THE MAP ENGINE ONLY WHEN A MAP IS OPENED. MapLibre, its stylesheet, its worker and
 *     the offline-tile reader are pulled in with dynamic `import()`s, so none of it lands in
 *     the bundle an operator downloads to watch the honeycomb.
 *
 *  2. SAY WHEN THERE IS NO BASEMAP TO DRAW. The source is probed up front and watched for tile
 *     errors afterwards; when it is gone the map says so plainly and offers a retry. Cameras
 *     are never hidden by it: the pins still draw, on a plain background, because knowing
 *     WHICH camera is alerting still matters when you can't see the street it's on.
 *
 *  3. KEEP THE BOARDS' OWN MARKERS — AND KEEP THEM GLUED TO THE MAP. Markers are rendered by
 *     the caller, in React, so the board reuses the floor plan's `MapMarker`, preview card and
 *     alert popover unchanged. Two overlay layers are offered:
 *       · `pins`    — positioned in LAYER space: pixels relative to a reference point, at the
 *                     zoom of the last settled view. While the map moves, the layer is
 *                     translated AND scaled to follow it — one style write per frame, no React
 *                     render — and `--map-k` carries the scale so content can counter-scale
 *                     and keep a constant screen size (MapMarker does). On every settle the
 *                     pins are re-projected and the scale returns to 1.
 *       · `overlay` — positioned in CONTAINER space (screen pixels), for the floating preview
 *                     card and hover popover, which need the viewport box to decide which
 *                     side of the pin to open on.
 *
 *  4. SPEAK THE OLD ZOOM SCALE. Saved default views, per-browser views and geocoder results
 *     are all in the classic 256-px-tile zoom scale. MapLibre counts zoom on 512-px tiles, one
 *     level lower for the same ground distance. Every zoom crossing this component's boundary
 *     is converted, so nothing stored had to change.
 *
 *  5. REPORT THE VIEW. Every settled move/zoom is handed back, which is what the board saves
 *     as the deployment default and remembers per browser.
 *
 * Rotation and pitch are off: the pin layer follows the map with a 2-D translate + scale,
 * which is exact for a north-up, flat map and wrong for any other.
 */
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type Ref,
} from 'react';
import type * as MapLibre from 'maplibre-gl';
import type { BasemapSource } from './basemaps';

/** A point in the map's own pixel space (layer) or in the container's (screen). */
export interface GeoPoint {
  x: number;
  y: number;
}

export interface GeoView {
  lat: number;
  lng: number;
  /** Classic 256-px-tile zoom — see point 4 in the file header. */
  zoom: number;
}

export interface GeoTileMapHandle {
  /** Move the view without a page round-trip — "go to the saved default", "frame the pins". */
  setView(view: GeoView): void;
  /** Frame every supplied coordinate. No-op for an empty list. */
  fitTo(points: Array<{ lat: number; lng: number }>): void;
  zoomBy(delta: number): void;
  /** The view as it stands right now — what the "set as default" action saves. */
  current(): GeoView | null;
  /**
   * A viewport pixel (a pointer event's clientX/clientY) back to a coordinate — what a pin
   * drag needs. It goes through the map's own container box rather than the caller's, because
   * the pin layer is transformed during a move and its offsets cannot be trusted mid-drag.
   */
  clientToLatLng(clientX: number, clientY: number): { lat: number; lng: number } | null;
}

/** MapLibre zoom + 1 = classic zoom. */
const ZOOM_OFFSET = 1;
const toGl = (z: number) => z - ZOOM_OFFSET;
const fromGl = (z: number) => z + ZOOM_OFFSET;
/** Deepest MapLibre zoom — street level, a little past the deepest tiles any source has. */
const MAX_GL_ZOOM = 19;
/** `fitTo` never frames tighter than this (MapLibre zoom) — one camera is not a rooftop. */
const FIT_MAX_GL_ZOOM = 17;
/** Programmatic moves glide rather than jump; short enough not to feel like waiting. */
const EASE_MS = 600;

/** How long a probe has to answer before the basemap is called unreachable. A LAN with no
 *  route out usually fails fast, but a captive portal or a slow uplink can hang. */
const PROBE_TIMEOUT_MS = 6000;

/** Consecutive tile failures before an initially-fine basemap is declared gone (a couple of
 *  missing tiles at the edge of coverage is normal and must not blank the map). */
const TILE_ERROR_LIMIT = 8;

/** Drawn until the real style arrives, and kept when it never does: the map still pans and
 *  zooms, and the pins still project, on a plain background. */
const BLANK_STYLE: MapLibre.StyleSpecification = { version: 8, sources: {}, layers: [] };

// ── Runtime, loaded once per page ───────────────────────────────────────────

let runtime: Promise<typeof MapLibre> | null = null;

function loadMapLibre(): Promise<typeof MapLibre> {
  if (!runtime) {
    runtime = (async () => {
      const [lib, , worker] = await Promise.all([
        import('maplibre-gl'),
        // @ts-ignore — a side-effect CSS import, resolved by the app's bundler. Not
        // `@ts-expect-error`: the apps pull in vite/client, which DOES declare `*.css`,
        // so there the directive would be unused and TS2578 would fail their typecheck.
        import('maplibre-gl/dist/maplibre-gl.css'),
        // @ts-ignore — Vite bundles the worker (and the shared chunk it imports) into one
        // file and hands back its URL. Same @ts-ignore reasoning as the CSS import above.
        import('maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'),
      ]);
      // MapLibre 6 looks for its worker NEXT TO its own module, which in a bundle is a
      // chunk with a hashed name — the worker is not there. Point it at the bundled one.
      lib.setWorkerUrl((worker as { default: string }).default);
      return lib;
    })();
    // A failed load (offline mid-deploy, a blocked chunk) must not poison every later map.
    runtime.catch(() => {
      runtime = null;
    });
  }
  return runtime;
}

let pmtilesProtocol: Promise<void> | null = null;

function ensurePmtiles(lib: typeof MapLibre): Promise<void> {
  if (!pmtilesProtocol) {
    pmtilesProtocol = import('pmtiles').then(({ Protocol }) => {
      lib.addProtocol('pmtiles', new Protocol().tile);
    });
  }
  return pmtilesProtocol;
}

/** XYZ template → MapLibre tile list. MapLibre has no `{s}`; spread it over the usual three. */
function tileList(template: string): string[] {
  const t = template.replace(/\{r\}/g, '');
  return t.includes('{s}') ? ['a', 'b', 'c'].map((s) => t.replace(/\{s\}/g, s)) : [t];
}

/** Overlay sources are named so their failures can be told apart from the base map's. */
const OVERLAY_PREFIX = 'overlay-';

async function styleFor(source: BasemapSource, lib: typeof MapLibre): Promise<MapLibre.StyleSpecification | string> {
  if (source.kind === 'style') return source.styleUrl;

  if (source.kind === 'raster') {
    const style: MapLibre.StyleSpecification = {
      version: 8,
      sources: {
        base: {
          type: 'raster',
          tiles: tileList(source.url),
          tileSize: 256,
          maxzoom: source.maxNativeZoom,
          attribution: source.attribution,
        },
      },
      layers: [{ id: 'base', type: 'raster', source: 'base' }],
    };
    (source.overlays ?? []).forEach((o, i) => {
      const id = `${OVERLAY_PREFIX}${i}`;
      style.sources[id] = {
        type: 'raster',
        tiles: tileList(o.url),
        tileSize: 256,
        maxzoom: source.maxNativeZoom,
        attribution: o.attribution,
      };
      style.layers.push({ id, type: 'raster', source: id });
    });
    return style;
  }

  // Offline vector tiles on this server: one archive read by HTTP range requests, plus the
  // fonts and icons the labels need, all from the same folder.
  await ensurePmtiles(lib);
  const { layers, namedFlavor } = await import('@protomaps/basemaps');
  const flavor = source.manifest.flavor ?? 'light';
  return {
    version: 8,
    glyphs: `${source.baseUrl}fonts/{fontstack}/{range}.pbf`,
    sprite: `${source.baseUrl}sprites/v4/${flavor}`,
    sources: {
      protomaps: {
        type: 'vector',
        url: `pmtiles://${source.baseUrl}${source.manifest.pmtiles}`,
        attribution:
          '<a href="https://protomaps.com">Protomaps</a> &copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a>',
      },
    },
    // English labels: MapLibre cannot shape Gujarati or Devanagari script correctly, and a
    // place name drawn with broken glyphs is worse than one in Latin letters.
    layers: layers('protomaps', namedFlavor(flavor), { lang: 'en' }),
  };
}

/** Can this browser reach the source at all? Asked before the first tile, so an isolated
 *  node says "no basemap" immediately instead of after a screen of failed requests. */
async function probe(source: BasemapSource): Promise<boolean> {
  if (source.kind === 'pmtiles') return true; // already answered by the manifest fetch
  if (source.kind === 'style') {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS);
    try {
      return (await fetch(source.styleUrl, { signal: ctl.signal })).ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }
  return new Promise<boolean>((resolve) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.src = '';
      resolve(false);
    }, PROBE_TIMEOUT_MS);
    img.onload = () => {
      clearTimeout(timer);
      resolve(true);
    };
    img.onerror = () => {
      clearTimeout(timer);
      resolve(false);
    };
    // Cross-origin without credentials: a tile is a public image, and asking for credentials
    // would fail CORS on most tile servers.
    img.crossOrigin = 'anonymous';
    img.src = tileList(source.url)[0]
      .replace(/\{z\}/g, '0')
      .replace(/\{x\}/g, '0')
      .replace(/\{y\}/g, '0');
  });
}

// ── Component ──────────────────────────────────────────────────────────────

export interface GeoTileMapProps {
  /** What to draw under the pins; null while the caller is still working it out. */
  source: BasemapSource | null;
  /** Where the map opens. Changing it does NOT move a map already on screen — the operator's
   *  current position is theirs; use the handle to move deliberately. */
  initialView: GeoView;
  /** Fired once the view settles (moveend), never mid-drag. */
  onViewChange?: (view: GeoView) => void;
  /** A click on the basemap itself (not on a pin). Used to place an armed camera. */
  onMapClick?: (lat: number, lng: number) => void;
  /** Crosshair cursor while a camera is armed for placement. */
  placing?: boolean;
  /**
   * Keep the SCREEN-space overlay glued to its anchor during a move.
   *
   * Off by default: the overlay's position has to be recomputed in React, and doing that on
   * every frame of every drag would cost a render per frame even with no card open. Turn it
   * on only while something is actually showing there.
   */
  trackOverlay?: boolean;
  /** Layer-space children: re-rendered when the view settles, carried by a transform while it
   *  moves. `project` maps a coordinate to layer pixels; `zoom` is the classic zoom. */
  pins?: (project: (lat: number, lng: number) => GeoPoint, zoom: number) => ReactNode;
  /** Screen-space children (preview card, popover). `project` maps to container pixels, and
   *  `size` is the map's box, for deciding which way a card should open. */
  overlay?: (
    project: (lat: number, lng: number) => GeoPoint,
    size: { w: number; h: number },
  ) => ReactNode;
  className?: string;
  handleRef?: Ref<GeoTileMapHandle>;
}

type BasemapState = 'loading' | 'ok' | 'unreachable';

/** The view the pin layer's coordinates are measured from. */
interface PinReference {
  /** MapLibre zoom the pins were projected at. */
  zoom: number;
  lng: number;
  lat: number;
  /** The same point in Web Mercator world units (0..1). */
  mx: number;
  my: number;
}

export default function GeoTileMap({
  source,
  initialView,
  onViewChange,
  onMapClick,
  placing,
  pins,
  overlay,
  trackOverlay = false,
  className = '',
  handleRef,
}: GeoTileMapProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapLibre.Map | null>(null);
  const libRef = useRef<typeof MapLibre | null>(null);
  const tileErrorsRef = useRef(0);
  const styleLoadedRef = useRef(false);

  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [basemap, setBasemap] = useState<BasemapState>('loading');
  // Bumped whenever the view SETTLES — the pins are re-projected and the layer transform
  // returns to identity. Not bumped per frame; the transform carries the pins meanwhile.
  const [projectionTick, setProjectionTick] = useState(0);
  // Bumped per frame while a screen-space card is open, so it tracks its anchor.
  const [screenTick, setScreenTick] = useState(0);
  const [size, setSize] = useState({ w: 0, h: 0 });
  // Probe counter: bumping it re-runs the reachability check behind the Retry button.
  const [probeAttempt, setProbeAttempt] = useState(0);

  /**
   * TWO REFERENCES, so a frame can never pair new positions with an old transform.
   *
   * `pending` is what the NEXT render projects against; `committed` is what the pins on screen
   * were projected against, and the only one the per-frame transform may use. It is promoted in
   * a layout effect — after React has written the new positions, before the browser paints.
   */
  const pendingRefPoint = useRef<PinReference | null>(null);
  const committedRefPoint = useRef<PinReference | null>(null);

  // The latest callbacks, so the map listeners can be bound once for the map's life instead
  // of being torn down and rebound every time the board re-renders.
  const onViewChangeRef = useRef(onViewChange);
  const onMapClickRef = useRef(onMapClick);
  const trackOverlayRef = useRef(trackOverlay);
  const overlayRafRef = useRef<number | null>(null);
  onViewChangeRef.current = onViewChange;
  onMapClickRef.current = onMapClick;
  trackOverlayRef.current = trackOverlay;

  /** Move the pin layer to where the map now is — the per-frame half of point 3. */
  const followMap = useCallback(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    const ref = committedRefPoint.current;
    if (!map || !layer || !ref) return;
    const at = map.project([ref.lng, ref.lat]);
    const k = 2 ** (map.getZoom() - ref.zoom);
    layer.style.transform = `translate(${at.x}px, ${at.y}px) scale(${k})`;
    layer.style.setProperty('--map-k', String(k));
  }, []);

  /** Re-anchor the pins on the view as it now stands. */
  const settle = useCallback(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (!map || !lib) return;
    const c = map.getCenter();
    const m = lib.MercatorCoordinate.fromLngLat(c);
    pendingRefPoint.current = { zoom: map.getZoom(), lng: c.lng, lat: c.lat, mx: m.x, my: m.y };
    setProjectionTick((n) => n + 1);
    setScreenTick((n) => n + 1);
  }, []);

  // ── Is there a basemap at all? ────────────────────────────────────────────

  const sourceKey = useMemo(() => (source ? JSON.stringify(source) : ''), [source]);

  useEffect(() => {
    let cancelled = false;
    tileErrorsRef.current = 0;
    if (!source) {
      setBasemap('loading');
      return () => {
        cancelled = true;
      };
    }
    if (source.kind !== 'pmtiles' && typeof navigator !== 'undefined' && navigator.onLine === false) {
      setBasemap('unreachable');
      return () => {
        cancelled = true;
      };
    }
    setBasemap('loading');
    probe(source).then((ok) => {
      if (!cancelled) setBasemap(ok ? 'ok' : 'unreachable');
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, probeAttempt]);

  // A node that comes back online should get its basemap back without a reload.
  useEffect(() => {
    const retry = () => setProbeAttempt((n) => n + 1);
    window.addEventListener('online', retry);
    return () => window.removeEventListener('online', retry);
  }, []);

  // ── The map itself ────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    let map: MapLibre.Map | null = null;

    (async () => {
      let lib: typeof MapLibre;
      try {
        lib = await loadMapLibre();
      } catch {
        if (!cancelled) setFailed(true);
        return;
      }
      if (cancelled || !hostRef.current) return;
      libRef.current = lib;

      let m: MapLibre.Map;
      try {
        m = new lib.Map({
          container: hostRef.current,
          style: BLANK_STYLE,
          center: [initialView.lng, initialView.lat],
          zoom: toGl(initialView.zoom),
          maxZoom: MAX_GL_ZOOM,
          // North-up and flat — see the file header.
          dragRotate: false,
          pitchWithRotate: false,
          touchPitch: false,
          maxPitch: 0,
          attributionControl: { compact: true },
        });
      } catch {
        // No WebGL: hardware acceleration off, or a remote desktop without a GPU.
        if (!cancelled) setFailed(true);
        return;
      }
      m.touchZoomRotate.disableRotation();
      m.keyboard.disableRotation();
      map = m;
      mapRef.current = m;

      m.on('move', () => {
        followMap();
        // The floating card can't ride the transform — it is anchored in screen space — so
        // it is re-projected in React, at most once per frame, and only while one is open.
        if (trackOverlayRef.current && overlayRafRef.current == null) {
          overlayRafRef.current = requestAnimationFrame(() => {
            overlayRafRef.current = null;
            setScreenTick((n) => n + 1);
          });
        }
      });
      m.on('moveend', () => {
        settle();
        const c = m.getCenter();
        onViewChangeRef.current?.({ lat: c.lat, lng: c.lng, zoom: fromGl(m.getZoom()) });
      });
      m.on('resize', () => {
        setSize({ w: m.getContainer().clientWidth, h: m.getContainer().clientHeight });
        settle();
      });
      m.on('click', (e: MapLibre.MapMouseEvent) => {
        onMapClickRef.current?.(e.lngLat.lat, e.lngLat.lng);
      });
      m.on('style.load', () => {
        styleLoadedRef.current = true;
      });
      m.on('error', (e: MapLibre.ErrorEvent & { sourceId?: string }) => {
        if (e.sourceId?.startsWith(OVERLAY_PREFIX)) return; // missing labels ≠ missing map
        if (!e.sourceId && styleLoadedRef.current) return; // a glyph or icon, not the map
        if (!e.sourceId) {
          // The style document itself could not be fetched.
          setBasemap('unreachable');
          return;
        }
        // Enough failures and the source is gone, not just thin at this zoom.
        tileErrorsRef.current += 1;
        if (tileErrorsRef.current >= TILE_ERROR_LIMIT) setBasemap('unreachable');
      });

      setSize({ w: hostRef.current.clientWidth, h: hostRef.current.clientHeight });
      settle();
      setReady(true);
    })();

    return () => {
      cancelled = true;
      if (overlayRafRef.current != null) cancelAnimationFrame(overlayRafRef.current);
      map?.remove();
      mapRef.current = null;
      setReady(false);
    };
    // Created ONCE. `initialView` is a starting position, not a binding — re-creating the
    // map when it changes would yank an operator back to the default mid-investigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The style is applied separately from the map, so switching Map / Satellite / Terrain (or
  // a site pointing at its own tile server) swaps the basemap without moving the view.
  useEffect(() => {
    const map = mapRef.current;
    const lib = libRef.current;
    if (!map || !lib || !ready || !source) return;
    let cancelled = false;
    tileErrorsRef.current = 0;
    styleFor(source, lib)
      .then((style) => {
        if (cancelled) return;
        styleLoadedRef.current = false;
        map.setStyle(style, { diff: false });
      })
      .catch(() => {
        if (!cancelled) setBasemap('unreachable');
      });
    return () => {
      cancelled = true;
    };
    // Re-applied on Retry too, so tiles that failed are asked for again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceKey, ready, probeAttempt]);

  // Crosshair while a camera is armed. On the canvas CONTAINER — MapLibre's own grab cursor
  // lives there and would win over anything set on the host.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getCanvasContainer().style.cursor = placing ? 'crosshair' : '';
  }, [placing, ready]);

  // MapLibre must be told when its box changes — it caches the size, and a stale one puts
  // every projection (and therefore every pin) at the wrong offset.
  useEffect(() => {
    const el = hostRef.current;
    if (!el || !ready) return;
    const ro = new ResizeObserver(() => mapRef.current?.resize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [ready]);

  /**
   * A WHEEL OVER A PIN STILL ZOOMS THE MAP.
   *
   * The pins sit above the map, not inside it — inside, every press on a pin would also start
   * a map pan, and a pin drag in edit mode would drag the world with it. The cost is that the
   * map never hears a wheel that lands on a pin, and on a dense board that is half the map.
   * So a wheel over the pin layer is handed to the map's canvas as if it had landed there.
   */
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !ready) return;
    const onWheel = (ev: WheelEvent) => {
      const canvas = mapRef.current?.getCanvas();
      if (!canvas) return;
      ev.preventDefault();
      canvas.dispatchEvent(
        new WheelEvent('wheel', {
          deltaX: ev.deltaX,
          deltaY: ev.deltaY,
          deltaZ: ev.deltaZ,
          deltaMode: ev.deltaMode,
          clientX: ev.clientX,
          clientY: ev.clientY,
          screenX: ev.screenX,
          screenY: ev.screenY,
          ctrlKey: ev.ctrlKey,
          shiftKey: ev.shiftKey,
          altKey: ev.altKey,
          metaKey: ev.metaKey,
          bubbles: true,
          cancelable: true,
        }),
      );
    };
    layer.addEventListener('wheel', onWheel, { passive: false });
    return () => layer.removeEventListener('wheel', onWheel);
  }, [ready]);

  useImperativeHandle(
    handleRef,
    (): GeoTileMapHandle => ({
      setView: (v) =>
        mapRef.current?.easeTo({ center: [v.lng, v.lat], zoom: toGl(v.zoom), duration: EASE_MS }),
      fitTo: (points) => {
        const lib = libRef.current;
        const map = mapRef.current;
        if (!lib || !map || !points.length) return;
        const bounds = new lib.LngLatBounds();
        points.forEach((p) => bounds.extend([p.lng, p.lat]));
        map.fitBounds(bounds, { padding: 48, maxZoom: FIT_MAX_GL_ZOOM, duration: EASE_MS });
      },
      zoomBy: (delta) => {
        const map = mapRef.current;
        if (map) map.zoomTo(map.getZoom() + delta, { duration: 250 });
      },
      current: () => {
        const map = mapRef.current;
        if (!map) return null;
        const c = map.getCenter();
        return { lat: c.lat, lng: c.lng, zoom: fromGl(map.getZoom()) };
      },
      clientToLatLng: (clientX, clientY) => {
        const map = mapRef.current;
        if (!map) return null;
        const r = map.getContainer().getBoundingClientRect();
        const ll = map.unproject([clientX - r.left, clientY - r.top]);
        return { lat: ll.lat, lng: ll.lng };
      },
    }),
    [],
  );

  /** Coordinate → layer pixels, against the pending reference. Recomputed once per settle. */
  const projectLayer = useCallback(
    (lat: number, lng: number): GeoPoint => {
      const lib = libRef.current;
      const ref = pendingRefPoint.current;
      if (!lib || !ref) return { x: 0, y: 0 };
      const m = lib.MercatorCoordinate.fromLngLat([lng, lat]);
      const worldPx = 512 * 2 ** ref.zoom;
      return { x: (m.x - ref.mx) * worldPx, y: (m.y - ref.my) * worldPx };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectionTick, ready],
  );

  /** Coordinate → container (screen) pixels, for the floating cards. */
  const projectContainer = useCallback(
    (lat: number, lng: number): GeoPoint => {
      const map = mapRef.current;
      if (!map) return { x: 0, y: 0 };
      const p = map.project([lng, lat]);
      return { x: p.x, y: p.y };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [screenTick, projectionTick, ready],
  );

  const pinZoom = pendingRefPoint.current ? fromGl(pendingRefPoint.current.zoom) : initialView.zoom;
  const pinLayer = useMemo(
    () => (ready && pins ? pins(projectLayer, pinZoom) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ready, pins, projectLayer],
  );

  // The pins on screen now match `pending` — promote it, and put the transform back to the
  // identity for it, before this frame is painted.
  useLayoutEffect(() => {
    committedRefPoint.current = pendingRefPoint.current;
    followMap();
  }, [pinLayer, followMap]);

  if (failed) {
    return (
      <div className={`relative flex h-full w-full items-center justify-center bg-muted ${className}`}>
        <p className="max-w-[46ch] px-4 text-center text-xs text-muted-foreground">
          This browser cannot draw the map — it needs WebGL, which is switched off or unavailable
          here (hardware acceleration disabled, or a remote desktop without a graphics card).
        </p>
      </div>
    );
  }

  return (
    /**
     * `isolate` — THE MAP'S Z-INDEXES MUST NOT LEAVE THE MAP.
     *
     * The map engine stacks its own controls and popups with z-indexes of its own. This box
     * was once `relative` with no stacking context, so all of that competed in whatever
     * ancestor context the board happened to sit in, at numbers no layout in this app uses.
     *
     * It won everything. Opening an event record over the Search page's tracking panel drew the
     * basemap straight through the modal (`z-50`) and its backdrop.
     *
     * `isolation: isolate` confines every one of those numbers to this element without changing
     * how anything inside it paints. Anything that must appear OVER the map is already rendered
     * inside it (the pin layer, the preview card, the basemap notice) and is unaffected.
     */
    <div className={`relative isolate h-full w-full overflow-hidden ${className}`}>
      {/* Position INLINE, not `absolute inset-0`: MapLibre tags this box `.maplibregl-map`,
          whose stylesheet sets `position: relative` and loads after the app's CSS — it beat
          the utility class, the box lost its height, and the map drew into nothing while
          the pins (outside it) still showed. */}
      <div ref={hostRef} className="bg-muted" style={{ position: 'absolute', inset: 0 }} />

      {/* Pins. Above the map's canvas, carried by the per-frame transform; pointer events are
          off on the layer itself so a drag that starts between pins still pans the map. */}
      <div
        ref={layerRef}
        className="pointer-events-none absolute left-0 top-0 z-[500]"
        style={{ width: 0, height: 0, transformOrigin: '0 0' }}
      >
        {/* Children re-enable pointer events on themselves — a pin is clickable, the empty space
            around it is the map. */}
        <div className="pointer-events-auto">{pinLayer}</div>
      </div>

      {/* Screen-space cards. Above the pins, and non-interactive except where a card opts in. */}
      {ready && overlay && (
        <div className="pointer-events-none absolute inset-0 z-[600]">
          {overlay(projectContainer, size)}
        </div>
      )}

      {source && basemap !== 'ok' && (
        <div className="pointer-events-none absolute inset-0 z-[400] flex items-start justify-center pt-6">
          <div className="pointer-events-auto rounded-lg border border-border bg-card px-4 py-3 text-center shadow-card">
            {basemap === 'loading' ? (
              <p className="text-xs text-muted-foreground">Checking for a basemap…</p>
            ) : (
              <>
                <p className="text-xs font-semibold text-foreground">{source.unavailableTitle}</p>
                <p className="mt-1 max-w-[42ch] text-[11px] leading-relaxed text-muted-foreground">
                  {source.unavailableText}
                </p>
                <button
                  type="button"
                  onClick={() => setProbeAttempt((n) => n + 1)}
                  className="mt-2 rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  Try again
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
